import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  useGetAdPackageQuery,
  useCreateAdPackageMutation,
  useUpdateAdPackageMutation,
  useSaveAdPackagePricesMutation,
  useGetCountriesQuery,
} from '../../../store/api/adminApi';
import PageLoader from '../../../components/PageLoader';
import SearchableSelect from '../../../components/SearchableSelect';
import { showSuccess, showError } from '../../../utils/toast';

interface Benefit {
  en: string;
  ar: string;
}

interface CountryPrice {
  country_id: number;
  country_name: string;
  amount: number;
}

const AdminAdPackageForm: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEdit = !!id;

  const [activeTab, setActiveTab] = useState<'general' | 'pricing'>('general');

  const { data, isLoading } = useGetAdPackageQuery(id!, { skip: !isEdit });
  const [createAdPackage, { isLoading: isCreating }] = useCreateAdPackageMutation();
  const [updateAdPackage, { isLoading: isUpdating }] = useUpdateAdPackageMutation();
  const [saveAdPackagePrices, { isLoading: isSavingPrices }] = useSaveAdPackagePricesMutation();

  const { data: countriesData } = useGetCountriesQuery({ per_page: 500 });
  const allCountries: any[] = countriesData?.data?.data || countriesData?.data || [];

  const [formData, setFormData] = useState({
    title: '',
    title_ar: '',
    duration: '',
    free_days: '',
    ad_refresh: '',
    amount: '',
    daily_rate: '',
    is_recommended: false,
    status: true,
  });

  const [benefits, setBenefits] = useState<Benefit[]>([{ en: '', ar: '' }]);

  // Country pricing state
  const [countryPrices, setCountryPrices] = useState<CountryPrice[]>([]);
  const [newCountryId, setNewCountryId] = useState('');
  const [newAmount, setNewAmount] = useState('');

  useEffect(() => {
    if (isEdit && data?.data) {
      const pkg = data.data;
      setFormData({
        title: pkg.title || '',
        title_ar: pkg.title_ar || '',
        duration: pkg.duration?.toString() || '',
        free_days: pkg.free_days?.toString() || '0',
        ad_refresh: pkg.ad_refresh?.toString() || '0',
        amount: pkg.amount?.toString() || '',
        daily_rate: pkg.daily_rate?.toString() || '',
        is_recommended: pkg.is_recommended || false,
        status: pkg.status !== undefined ? pkg.status : true,
      });

      if (pkg.benefits && Array.isArray(pkg.benefits) && pkg.benefits.length > 0) {
        setBenefits(pkg.benefits);
      } else {
        setBenefits([{ en: '', ar: '' }]);
      }

      if (pkg.country_prices && Array.isArray(pkg.country_prices)) {
        setCountryPrices(
          pkg.country_prices.map((cp: any) => ({
            country_id:   cp.country_id,
            country_name: cp.country?.name || '',
            amount:       parseFloat(cp.amount),
          }))
        );
      }
    }
  }, [isEdit, data]);

  // Auto-calc daily rate
  useEffect(() => {
    if (formData.amount && formData.duration) {
      const amount = parseFloat(formData.amount);
      const duration = parseInt(formData.duration);
      if (amount > 0 && duration > 0) {
        setFormData(prev => ({ ...prev, daily_rate: (amount / duration).toFixed(2) }));
      }
    }
  }, [formData.amount, formData.duration]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleBenefitChange = (index: number, field: 'en' | 'ar', value: string) => {
    const updated = [...benefits];
    updated[index] = { ...updated[index], [field]: value };
    setBenefits(updated);
  };

  const addBenefit = () => setBenefits([...benefits, { en: '', ar: '' }]);

  const removeBenefit = (index: number) => {
    if (benefits.length > 1) {
      setBenefits(benefits.filter((_, i) => i !== index));
    } else {
      showError('At least one benefit is required.');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const trimmedTitle   = formData.title.trim();
    const trimmedTitleAr = formData.title_ar.trim();

    if (!trimmedTitle || trimmedTitle.length < 3) {
      showError('Title is required and must be at least 3 characters long.');
      return;
    }
    if (!trimmedTitleAr || trimmedTitleAr.length < 3) {
      showError('Title Arabic is required and must be at least 3 characters long.');
      return;
    }
    if (!formData.duration || parseInt(formData.duration) < 1) {
      showError('Duration is required and must be at least 1 day.');
      return;
    }
    if (!formData.amount || parseFloat(formData.amount) < 0) {
      showError('Amount is required and must be greater than or equal to 0.');
      return;
    }

    const validBenefits = benefits.filter(b => b.en.trim() && b.ar.trim());
    if (validBenefits.length === 0) {
      showError('At least one benefit with both English and Arabic text is required.');
      return;
    }

    try {
      const submitData = {
        title:          trimmedTitle,
        title_ar:       trimmedTitleAr,
        duration:       parseInt(formData.duration),
        free_days:      parseInt(formData.free_days) || 0,
        ad_refresh:     parseInt(formData.ad_refresh) || 0,
        amount:         parseFloat(formData.amount),
        daily_rate:     formData.daily_rate ? parseFloat(formData.daily_rate) : null,
        benefits:       validBenefits,
        is_recommended: formData.is_recommended,
        status:         formData.status,
      };

      if (isEdit) {
        const result = await updateAdPackage({ id: parseInt(id!), data: submitData }).unwrap();
        showSuccess(result.message || 'Ad Package updated successfully!');
      } else {
        const result = await createAdPackage(submitData).unwrap();
        showSuccess(result.message || 'Ad Package created successfully!');
      }

      navigate('/admin/ad-packages');
    } catch (err: any) {
      if (err?.data?.errors) {
        const errorMessages = Object.values(err.data.errors).flat().join(', ');
        showError(errorMessages || err?.data?.message || 'Validation failed');
      } else {
        showError(err?.data?.message || `Failed to ${isEdit ? 'update' : 'create'} ad package. Please try again.`);
      }
    }
  };

  // Country that are already added (to exclude from dropdown)
  const usedCountryIds = new Set(countryPrices.map(p => p.country_id));
  const availableCountries = allCountries.filter(c => !usedCountryIds.has(c.id));

  const addCountryPrice = () => {
    if (!newCountryId) { showError('Please select a country.'); return; }
    if (!newAmount || parseFloat(newAmount) < 0) { showError('Please enter a valid amount.'); return; }

    const country = allCountries.find(c => c.id === parseInt(newCountryId));
    setCountryPrices(prev => [
      ...prev,
      { country_id: parseInt(newCountryId), country_name: country?.name || '', amount: parseFloat(newAmount) },
    ]);
    setNewCountryId('');
    setNewAmount('');
  };

  const removeCountryPrice = (countryId: number) => {
    setCountryPrices(prev => prev.filter(p => p.country_id !== countryId));
  };

  const updateCountryPriceAmount = (countryId: number, value: string) => {
    setCountryPrices(prev =>
      prev.map(p => p.country_id === countryId ? { ...p, amount: parseFloat(value) || 0 } : p)
    );
  };

  const handleSavePricing = async () => {
    if (!isEdit) return;
    try {
      const result = await saveAdPackagePrices({
        id:     parseInt(id!),
        prices: countryPrices.map(p => ({ country_id: p.country_id, amount: p.amount })),
      }).unwrap();
      showSuccess(result.message || 'Pricing saved successfully!');
    } catch (err: any) {
      showError(err?.data?.message || 'Failed to save pricing.');
    }
  };

  if (isLoading) return <PageLoader />;

  return (
    <div className="app-main flex-column flex-row-fluid" id="kt_app_main">
      <div className="d-flex flex-column flex-column-fluid">
        <div id="kt_app_toolbar" className="app-toolbar py-3 py-lg-6">
          <div id="kt_app_toolbar_container" className="app-container container-xxl d-flex flex-stack">
            <div className="page-title d-flex flex-column justify-content-center flex-wrap me-3">
              <h1 className="page-heading d-flex text-dark fw-bold fs-3 flex-column justify-content-center my-0">
                {isEdit ? 'Edit Ad Package' : 'Add New Ad Package'}
              </h1>
              <ul className="breadcrumb breadcrumb-separatorless fw-semibold fs-7 my-0 pt-1">
                <li className="breadcrumb-item text-muted">
                  <a href="#" onClick={(e) => { e.preventDefault(); navigate('/admin/dashboard'); }} className="text-muted text-hover-primary">Dashboard</a>
                </li>
                <li className="breadcrumb-item"><span className="bullet bg-gray-400 w-5px h-2px"></span></li>
                <li className="breadcrumb-item text-muted">
                  <a href="#" onClick={(e) => { e.preventDefault(); navigate('/admin/ad-packages'); }} className="text-muted text-hover-primary">Ad Packages</a>
                </li>
              </ul>
            </div>
          </div>
        </div>

        <div id="kt_app_content" className="app-content flex-column-fluid">
          <div id="kt_app_content_container" className="app-container container-xxl">
            <div className="card">
              <div className="card-header border-0 pt-6">
                <ul className="nav nav-tabs nav-line-tabs nav-line-tabs-2x fs-6 fw-semibold border-0">
                  <li className="nav-item">
                    <button
                      className={`nav-link px-4 py-3${activeTab === 'general' ? ' active text-primary' : ' text-muted'}`}
                      onClick={() => setActiveTab('general')}
                      type="button"
                    >
                      General
                    </button>
                  </li>
                  {isEdit && (
                    <li className="nav-item">
                      <button
                        className={`nav-link px-4 py-3${activeTab === 'pricing' ? ' active text-primary' : ' text-muted'}`}
                        onClick={() => setActiveTab('pricing')}
                        type="button"
                      >
                        Pricing by Country
                        {countryPrices.length > 0 && (
                          <span className="badge badge-sm ms-2" style={{ background: 'rgba(57,106,84,0.15)', color: '#396A54' }}>
                            {countryPrices.length}
                          </span>
                        )}
                      </button>
                    </li>
                  )}
                </ul>
              </div>

              <div className="card-body pt-4">

                {/* ── General Tab ─────────────────────────────────────────── */}
                {activeTab === 'general' && (
                  <form onSubmit={handleSubmit} className="form fv-plugins-bootstrap5 fv-plugins-framework">
                    <div className="row g-9 my-3">
                      <div className="col-md-6 fv-row fv-plugins-icon-container">
                        <label className="required fs-6 fw-semibold mb-2">Title</label>
                        <input
                          type="text"
                          className="form-control form-control-solid"
                          placeholder="Enter Package Title"
                          name="title"
                          value={formData.title}
                          onChange={handleInputChange}
                          required
                        />
                      </div>

                      <div className="col-md-6 fv-row fv-plugins-icon-container">
                        <label className="required fs-6 fw-semibold mb-2">Title Arabic</label>
                        <input
                          type="text"
                          className="form-control form-control-solid"
                          placeholder="Enter Package Title in Arabic"
                          name="title_ar"
                          value={formData.title_ar}
                          onChange={handleInputChange}
                          required
                        />
                      </div>

                      <div className="col-md-4 fv-row fv-plugins-icon-container">
                        <label className="required fs-6 fw-semibold mb-2">Duration (Days)</label>
                        <input
                          type="number"
                          className="form-control form-control-solid"
                          placeholder="Enter Duration"
                          name="duration"
                          value={formData.duration}
                          onChange={handleInputChange}
                          min="1"
                          required
                        />
                      </div>

                      <div className="col-md-4 fv-row fv-plugins-icon-container">
                        <label className="fs-6 fw-semibold mb-2">Free Days (Bonus)</label>
                        <input
                          type="number"
                          className="form-control form-control-solid"
                          placeholder="Enter Free Days"
                          name="free_days"
                          value={formData.free_days}
                          onChange={handleInputChange}
                          min="0"
                        />
                      </div>

                      <div className="col-md-4 fv-row fv-plugins-icon-container">
                        <label className="required fs-6 fw-semibold mb-2">Default Amount ($)</label>
                        <input
                          type="number"
                          step="0.01"
                          className="form-control form-control-solid"
                          placeholder="Enter Amount"
                          name="amount"
                          value={formData.amount}
                          onChange={handleInputChange}
                          min="0"
                          required
                        />
                        <div className="form-text">Used when no country-specific price is set</div>
                      </div>

                      <div className="col-md-4 fv-row fv-plugins-icon-container">
                        <label className="fs-6 fw-semibold mb-2">Daily Rate ($/day)</label>
                        <input
                          type="number"
                          step="0.01"
                          className="form-control form-control-solid"
                          placeholder="Auto-calculated from Amount/Duration"
                          name="daily_rate"
                          value={formData.daily_rate}
                          onChange={handleInputChange}
                          min="0"
                        />
                        <div className="form-text">Auto-calculated from Amount ÷ Duration, but can be manually adjusted</div>
                      </div>

                      <div className="col-md-4 fv-row fv-plugins-icon-container">
                        <label className="fs-6 fw-semibold mb-2">Ad Refresh</label>
                        <input
                          type="number"
                          className="form-control form-control-solid"
                          placeholder="Enter Ad Refresh"
                          name="ad_refresh"
                          value={formData.ad_refresh}
                          onChange={handleInputChange}
                          min="0"
                        />
                      </div>

                      <div className="col-md-4 fv-row fv-plugins-icon-container">
                        <div className="d-flex align-items-center mt-8">
                          <div className="form-check form-switch form-check-custom form-check-solid me-5">
                            <input
                              className="form-check-input"
                              type="checkbox"
                              name="is_recommended"
                              checked={formData.is_recommended}
                              onChange={handleInputChange}
                              id="is_recommended"
                            />
                            <label className="form-check-label fs-6 fw-semibold" htmlFor="is_recommended">
                              Recommended Package
                            </label>
                          </div>
                        </div>
                        <div className="d-flex align-items-center mt-3">
                          <div className="form-check form-switch form-check-custom form-check-solid me-5">
                            <input
                              className="form-check-input"
                              type="checkbox"
                              name="status"
                              checked={formData.status}
                              onChange={handleInputChange}
                              id="status"
                            />
                            <label className="form-check-label fs-6 fw-semibold" htmlFor="status">
                              Active Status
                            </label>
                          </div>
                        </div>
                      </div>

                      <div className="col-12">
                        <div className="d-flex justify-content-between align-items-center mb-3">
                          <label className="required fs-6 fw-semibold mb-0">Benefits</label>
                          <button type="button" className="btn btn-sm btn-light-primary" onClick={addBenefit}>
                            <span className="svg-icon svg-icon-2">
                              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <rect opacity="0.5" x="11.364" y="20.364" width="16" height="2" rx="1" transform="rotate(-90 11.364 20.364)" fill="currentColor"></rect>
                                <rect x="4.36396" y="11.364" width="16" height="2" rx="1" fill="currentColor"></rect>
                              </svg>
                            </span>
                            Add Benefit
                          </button>
                        </div>

                        <div className="table-responsive">
                          <table className="table table-bordered align-middle">
                            <thead>
                              <tr>
                                <th className="fw-semibold">English</th>
                                <th className="fw-semibold">Arabic</th>
                                <th className="text-center" style={{ width: 80 }}>Action</th>
                              </tr>
                            </thead>
                            <tbody>
                              {benefits.map((benefit, index) => (
                                <tr key={index}>
                                  <td>
                                    <input
                                      type="text"
                                      className="form-control form-control-solid"
                                      placeholder="Enter benefit in English"
                                      value={benefit.en}
                                      onChange={(e) => handleBenefitChange(index, 'en', e.target.value)}
                                      required
                                    />
                                  </td>
                                  <td>
                                    <input
                                      type="text"
                                      className="form-control form-control-solid"
                                      placeholder="Enter benefit in Arabic"
                                      value={benefit.ar}
                                      onChange={(e) => handleBenefitChange(index, 'ar', e.target.value)}
                                      required
                                    />
                                  </td>
                                  <td className="text-center">
                                    {benefits.length > 1 && (
                                      <button
                                        type="button"
                                        className="btn btn-sm btn-icon btn-light-danger"
                                        onClick={() => removeBenefit(index)}
                                        title="Remove"
                                      >
                                        <span className="svg-icon svg-icon-2">
                                          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                            <rect opacity="0.5" x="7.05025" y="15.5356" width="12" height="2" rx="1" transform="rotate(-45 7.05025 15.5356)" fill="currentColor"></rect>
                                            <rect x="8.46436" y="7.05029" width="12" height="2" rx="1" transform="rotate(45 8.46436 7.05029)" fill="currentColor"></rect>
                                          </svg>
                                        </span>
                                      </button>
                                    )}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </div>

                    <div className="text-center">
                      <button type="button" className="btn btn-light me-3" onClick={() => navigate('/admin/ad-packages')}>
                        Discard
                      </button>
                      <button type="submit" className="btn btn-base" disabled={isCreating || isUpdating}>
                        <span className="indicator-label">Submit</span>
                        {(isCreating || isUpdating) && (
                          <span className="indicator-progress">
                            Please wait... <span className="spinner-border spinner-border-sm align-middle ms-2"></span>
                          </span>
                        )}
                      </button>
                    </div>
                  </form>
                )}

                {/* ── Pricing Tab ──────────────────────────────────────────── */}
                {activeTab === 'pricing' && isEdit && (
                  <div>
                    {/* Default price reminder */}
                    <div
                      className="alert d-flex align-items-center mb-6 py-4"
                      style={{ background: 'rgba(57,106,84,0.07)', border: '1px solid rgba(57,106,84,0.2)', borderRadius: 8 }}
                    >
                      <span className="fs-6 text-dark">
                        <strong>Default price:</strong> ${parseFloat(formData.amount || '0').toFixed(2)} — applied when no country-specific price is set.
                      </span>
                    </div>

                    {/* Add new country price */}
                    <div className="card border mb-6">
                      <div className="card-header min-h-50px px-6 border-bottom">
                        <span className="card-title fw-bold fs-6">Add Country Price Override</span>
                      </div>
                      <div className="card-body px-6 py-5">
                        <div className="row g-4 align-items-end">
                          <div className="col-12 col-md-5">
                            <label className="form-label fw-semibold fs-7 text-uppercase text-muted mb-2">Country</label>
                            <SearchableSelect
                              options={availableCountries.map((c: any) => ({ value: c.id, label: c.name }))}
                              value={newCountryId}
                              onChange={(v) => setNewCountryId(String(v))}
                              placeholder="Select country…"
                            />
                          </div>
                          <div className="col-12 col-md-4">
                            <label className="form-label fw-semibold fs-7 text-uppercase text-muted mb-2">Price ($)</label>
                            <input
                              type="number"
                              step="0.01"
                              min="0"
                              className="form-control form-control-solid"
                              placeholder="0.00"
                              value={newAmount}
                              onChange={(e) => setNewAmount(e.target.value)}
                            />
                          </div>
                          <div className="col-12 col-md-3">
                            <button
                              type="button"
                              className="btn btn-base w-100"
                              onClick={addCountryPrice}
                              disabled={!newCountryId || !newAmount}
                            >
                              Add
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Country prices table */}
                    {countryPrices.length > 0 ? (
                      <div className="table-responsive mb-6">
                        <table className="table table-row-dashed table-row-gray-200 align-middle gs-0 gy-4">
                          <thead>
                            <tr className="border-0">
                              <th className="p-0 ps-4 text-muted fw-semibold fs-7 text-uppercase">Country</th>
                              <th className="p-0 text-muted fw-semibold fs-7 text-uppercase">Price ($)</th>
                              <th className="p-0 text-muted fw-semibold fs-7 text-uppercase">vs. Default</th>
                              <th className="p-0 text-end pe-4 text-muted fw-semibold fs-7 text-uppercase">Action</th>
                            </tr>
                          </thead>
                          <tbody>
                            {countryPrices.map((cp) => {
                              const defaultAmt = parseFloat(formData.amount || '0');
                              const diff = defaultAmt > 0 ? ((cp.amount - defaultAmt) / defaultAmt * 100).toFixed(1) : null;
                              return (
                                <tr key={cp.country_id}>
                                  <td className="ps-4">
                                    <span className="text-dark fw-bold fs-6">{cp.country_name}</span>
                                  </td>
                                  <td>
                                    <input
                                      type="number"
                                      step="0.01"
                                      min="0"
                                      className="form-control form-control-solid form-control-sm"
                                      style={{ width: 120 }}
                                      value={cp.amount}
                                      onChange={(e) => updateCountryPriceAmount(cp.country_id, e.target.value)}
                                    />
                                  </td>
                                  <td>
                                    {diff !== null ? (
                                      <span
                                        className="badge fw-semibold fs-8"
                                        style={{
                                          background: parseFloat(diff) < 0
                                            ? 'rgba(239,68,68,0.10)'
                                            : parseFloat(diff) > 0
                                              ? 'rgba(34,197,94,0.10)'
                                              : 'rgba(100,116,139,0.10)',
                                          color: parseFloat(diff) < 0
                                            ? '#dc2626'
                                            : parseFloat(diff) > 0
                                              ? '#16a34a'
                                              : '#475569',
                                        }}
                                      >
                                        {parseFloat(diff) >= 0 ? '+' : ''}{diff}%
                                      </span>
                                    ) : '—'}
                                  </td>
                                  <td className="text-end pe-4">
                                    <button
                                      type="button"
                                      className="btn btn-sm btn-icon btn-light-danger"
                                      onClick={() => removeCountryPrice(cp.country_id)}
                                      title="Remove"
                                    >
                                      <span className="svg-icon svg-icon-2">
                                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                          <rect opacity="0.5" x="7.05025" y="15.5356" width="12" height="2" rx="1" transform="rotate(-45 7.05025 15.5356)" fill="currentColor"></rect>
                                          <rect x="8.46436" y="7.05029" width="12" height="2" rx="1" transform="rotate(45 8.46436 7.05029)" fill="currentColor"></rect>
                                        </svg>
                                      </span>
                                    </button>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <div
                        className="d-flex align-items-center justify-content-center py-12 mb-6 rounded"
                        style={{ background: '#f9f9f9', border: '1px dashed #e0e0e0' }}
                      >
                        <span className="text-muted fs-6">No country-specific prices yet. All users will see the default price.</span>
                      </div>
                    )}

                    <div className="text-end">
                      <button type="button" className="btn btn-light me-3" onClick={() => navigate('/admin/ad-packages')}>
                        Discard
                      </button>
                      <button
                        type="button"
                        className="btn btn-base"
                        onClick={handleSavePricing}
                        disabled={isSavingPrices}
                      >
                        {isSavingPrices ? (
                          <>
                            Saving… <span className="spinner-border spinner-border-sm align-middle ms-2"></span>
                          </>
                        ) : (
                          'Save Pricing'
                        )}
                      </button>
                    </div>
                  </div>
                )}

              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminAdPackageForm;
